/**
 * RecommendPartnerDialog — Partner-Kontaktdaten (Werkstatt, Bank, Versicherung,
 * Garantiegeber, Lieferanten, Dienstleister) per WhatsApp / E-Mail / SMS an einen
 * Kunden (aus dem CRM) weiterleiten.
 *
 * Verwendung:
 *  - Mit festem Partner:  <RecommendPartnerDialog open partner={p} onClose={...} />
 *  - Ohne Partner (Auswahl im Dialog, z. B. aus der Auftragserfassung):
 *      <RecommendPartnerDialog open onClose={...} defaultCustomer={{...}} />
 */
import { useState, useEffect } from "react";
import { Loader2, Send, Search, Mail, MessageCircle, Phone, Building2, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import api from "../../lib/api";
import { toast } from "sonner";

const CHANNELS = [
    { key: "whatsapp", label: "WhatsApp", Icon: MessageCircle },
    { key: "email", label: "E-Mail", Icon: Mail },
    { key: "sms", label: "SMS", Icon: Phone },
];

const custName = (c) => c?.company_name || `${c?.first_name || ""} ${c?.last_name || ""}`.trim() || c?.name || c?.email || "Kunde";
const partnerContactLines = (p) => {
    if (!p) return [];
    const phone = p.contact_phone || p.phone || "";
    const email = p.contact_email || "";
    const addr = [p.street, [p.postal_code, p.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
    return [
        `${p.name} (${p.branch_label || p.branch || "—"})`,
        phone && `Telefon: ${phone}`,
        email && `E-Mail: ${email}`,
        addr && `Adresse: ${addr}`,
        p.website && `Web: ${p.website}`,
    ].filter(Boolean);
};

export function RecommendPartnerDialog({ open, onClose, partner = null, defaultCustomer = null }) {
    const [partners, setPartners] = useState([]);
    const [partnerQuery, setPartnerQuery] = useState("");
    const [selectedPartner, setSelectedPartner] = useState(partner);
    const [custQuery, setCustQuery] = useState("");
    const [custResults, setCustResults] = useState([]);
    const [customer, setCustomer] = useState(defaultCustomer);
    const [channel, setChannel] = useState("whatsapp");
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open) return;
        setSelectedPartner(partner);
        setCustomer(defaultCustomer);
        setChannel("whatsapp");
        setMessage("");
        setCustQuery(""); setCustResults([]); setPartnerQuery("");
    }, [open, partner, defaultCustomer]);

    // Partner laden (nur falls keiner fest vorgegeben)
    useEffect(() => {
        if (!open || partner) return;
        (async () => {
            try { const { data } = await api.get("/dealers/me/partners"); setPartners(data.items || []); }
            catch { setPartners([]); }
        })();
    }, [open, partner]);

    // Kundensuche (CRM, debounced)
    useEffect(() => {
        if (!open) return;
        const t = setTimeout(async () => {
            const q = custQuery.trim();
            if (q.length < 2) { setCustResults([]); return; }
            try { const { data } = await api.get("/customers", { params: { q, limit: 8 } }); setCustResults(data || []); }
            catch { setCustResults([]); }
        }, 250);
        return () => clearTimeout(t);
    }, [custQuery, open]);

    const filteredPartners = partnerQuery.trim()
        ? partners.filter((p) => (`${p.name} ${p.branch_label || p.branch || ""} ${p.city || ""}`).toLowerCase().includes(partnerQuery.trim().toLowerCase()))
        : partners;

    const recipient = customer ? (channel === "email" ? (customer.email || "") : (customer.phone || "")) : "";

    const send = async () => {
        if (!selectedPartner) { toast.error("Bitte Partner wählen"); return; }
        if (!customer) { toast.error("Bitte Kunde wählen"); return; }
        if (!recipient) { toast.error(channel === "email" ? "Kunde hat keine E-Mail hinterlegt" : "Kunde hat keine Telefonnummer hinterlegt"); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/dealers/me/partners/${selectedPartner.id}/recommend`, {
                channel, recipient, customer_id: customer.id || null, customer_name: custName(customer), message: message.trim() || null,
            });
            if (channel === "whatsapp" && data.wa_url) {
                window.open(data.wa_url, "_blank");
                toast.success("WhatsApp geöffnet — Nachricht ist vorausgefüllt");
            } else if (data.mocked) {
                toast.success(`Versand simuliert (kein Live-Key) · ${channel.toUpperCase()}`);
            } else {
                toast.success(`Kontakt gesendet via ${channel.toUpperCase()}`);
            }
            onClose?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Senden fehlgeschlagen");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="recommend-partner-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-amber-600" /> Partner an Kunden senden</DialogTitle>
                </DialogHeader>
                <DialogDescription className="sr-only">Partner-Kontaktdaten per WhatsApp, E-Mail oder SMS an einen Kunden weiterleiten.</DialogDescription>

                {/* 1) Partner */}
                {partner ? (
                    <div className="rounded-sm border border-amber-200 bg-amber-50 p-2.5 text-sm" data-testid="recommend-fixed-partner">
                        {partnerContactLines(selectedPartner).map((l, i) => (
                            <div key={i} className={i === 0 ? "font-bold text-gray-800" : "text-gray-600 text-xs"}>{l}</div>
                        ))}
                    </div>
                ) : (
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Partner wählen</label>
                        {selectedPartner ? (
                            <div className="flex items-center justify-between gap-2 rounded-sm border border-amber-200 bg-amber-50 p-2.5 text-sm" data-testid="recommend-selected-partner">
                                <div className="min-w-0">
                                    <div className="font-bold text-gray-800 truncate">{selectedPartner.name}</div>
                                    <div className="text-xs text-gray-500 truncate">{selectedPartner.branch_label || selectedPartner.branch}</div>
                                </div>
                                <button onClick={() => setSelectedPartner(null)} className="text-xs font-bold text-amber-700 hover:underline shrink-0" data-testid="recommend-change-partner">ändern</button>
                            </div>
                        ) : (
                            <>
                                <div className="relative">
                                    <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <Input value={partnerQuery} onChange={(e) => setPartnerQuery(e.target.value)} placeholder="Partner suchen (Name, Branche, Ort)…" className="rounded-sm pl-8 h-9" data-testid="recommend-partner-search" />
                                </div>
                                <div className="max-h-40 overflow-y-auto border border-gray-100 rounded-sm divide-y divide-gray-100" data-testid="recommend-partner-list">
                                    {filteredPartners.length === 0 ? (
                                        <div className="px-2.5 py-2 text-xs text-gray-400">Keine Partner gefunden.</div>
                                    ) : filteredPartners.slice(0, 30).map((p) => (
                                        <button key={p.id} type="button" onClick={() => setSelectedPartner(p)}
                                            className="w-full text-left px-2.5 py-2 hover:bg-amber-50 text-sm flex items-center justify-between gap-2"
                                            data-testid={`recommend-partner-opt-${p.id}`}>
                                            <span className="truncate"><b>{p.name}</b> <span className="text-gray-400">· {p.branch_label || p.branch}</span></span>
                                            <span className="shrink-0 text-[11px] text-gray-500">{p.city || ""}</span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* 2) Kunde */}
                <div className="space-y-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Kunde (Empfänger)</label>
                    {customer ? (
                        <div className="flex items-center justify-between gap-2 rounded-sm border border-gray-200 bg-gray-50 p-2.5 text-sm" data-testid="recommend-selected-customer">
                            <div className="min-w-0">
                                <div className="font-bold text-gray-800 truncate">{custName(customer)}</div>
                                <div className="text-xs text-gray-500 truncate">{customer.email || "ohne E-Mail"} · {customer.phone || "ohne Telefon"}</div>
                            </div>
                            <button onClick={() => { setCustomer(null); setCustQuery(""); }} className="text-xs font-bold text-gray-600 hover:underline shrink-0" data-testid="recommend-change-customer">ändern</button>
                        </div>
                    ) : (
                        <>
                            <div className="relative">
                                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                <Input value={custQuery} onChange={(e) => setCustQuery(e.target.value)} placeholder="Kunde suchen (Name, E-Mail, Telefon)…" className="rounded-sm pl-8 h-9" data-testid="recommend-customer-search" />
                            </div>
                            {custResults.length > 0 && (
                                <div className="max-h-40 overflow-y-auto border border-gray-100 rounded-sm divide-y divide-gray-100" data-testid="recommend-customer-list">
                                    {custResults.map((c) => (
                                        <button key={c.id} type="button" onClick={() => setCustomer(c)}
                                            className="w-full text-left px-2.5 py-2 hover:bg-gray-50 text-sm"
                                            data-testid={`recommend-customer-opt-${c.id}`}>
                                            <div className="font-bold text-gray-800 truncate">{custName(c)}</div>
                                            <div className="text-xs text-gray-500 truncate">{c.email || "ohne E-Mail"} · {c.phone || "ohne Telefon"}</div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* 3) Kanal */}
                <div className="space-y-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Kanal</label>
                    <div className="flex gap-2" data-testid="recommend-channels">
                        {CHANNELS.map(({ key, label, Icon }) => (
                            <button key={key} type="button" onClick={() => setChannel(key)}
                                className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-sm border px-2 py-2 text-sm font-bold ${channel === key ? "border-[#111827] bg-[#111827] text-white" : "border-gray-200 bg-white text-gray-600 hover:border-gray-400"}`}
                                data-testid={`recommend-channel-${key}`}>
                                {channel === key ? <Check className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />} {label}
                            </button>
                        ))}
                    </div>
                    {customer && !recipient && (
                        <p className="text-[11px] text-red-600" data-testid="recommend-no-recipient">
                            Kunde hat {channel === "email" ? "keine E-Mail" : "keine Telefonnummer"} hinterlegt.
                        </p>
                    )}
                </div>

                {/* 4) Nachricht */}
                <div className="space-y-1.5">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Nachricht (optional)</label>
                    <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
                        placeholder="Eigener Einleitungstext — die Kontaktdaten werden automatisch angehängt."
                        className="w-full border border-gray-300 rounded-sm px-2.5 py-2 text-sm" data-testid="recommend-message" />
                </div>

                <Button onClick={send} disabled={busy || !selectedPartner || !customer || !recipient}
                    className="w-full rounded-sm font-bold bg-[#FFCC00] hover:bg-[#e6b800] text-[#111827]" data-testid="recommend-send-btn">
                    {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                    {channel === "whatsapp" ? "WhatsApp öffnen" : "Senden"}
                </Button>
            </DialogContent>
        </Dialog>
    );
}
